import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { Box } from '@mui/system'
import ProtectedRouteError from './UnsubscribeProtectedRouteError'
import { getUserInfo, getCemFeatures } from '../../store/userInfo/userInfoSlice'
import { isFeatureFlagEnabled } from '../../rsmCoreComponents/utils/featureFlagUtils'
import { COMMUNICATIONSERVICE_BASE_URL } from '../../envVariables'

const UnsubscribeProtectedRoute = ({
  children,
  queryParams = undefined,
  matchType,
}: {
  children: React.ReactNode
  queryParams?: string[] | string
  matchType: 'any' | 'all'
}) => {
  const userInfo = useSelector(getUserInfo)
  const cemFeatures = useSelector(getCemFeatures)
  const [searchParams] = useSearchParams()
  const [isLoading, setIsLoading] = useState(true)
  const [hasAccess, setHasAccess] = useState<boolean>(false)

  useEffect(() => {
    const validateAccess = async () => {
      if (!queryParams) {
        throw new Error(
          'queryParams must be specified on the UnsubscribeProtectedRoute component.',
        )
      }

      // Check if the user must have a specified Azure feature flag.
      const featureFlagEnabled = isFeatureFlagEnabled(
        'EmailUnsubscribe',
        userInfo.emailAddress,
        cemFeatures,
        matchType,
      )
      if (!featureFlagEnabled) {
        setHasAccess(false)
        setIsLoading(false)
      }

      // Check if the user must have specific search parameters.
      if (queryParams && queryParams.length > 0 && searchParams) {
        let allParamsHaveValues = false
        const hasAllParams = Array.isArray(queryParams)
          ? queryParams.every((param) => searchParams.has(param))
          : typeof queryParams === 'string' && searchParams.has(queryParams)

        if (Array.isArray(queryParams)) {
          allParamsHaveValues = queryParams.every((param) => {
            const value = searchParams.get(param)
            return value !== '' && value !== null
          })
        } else if (typeof queryParams === 'string') {
          const value = searchParams.get(queryParams)
          allParamsHaveValues = value !== '' && value !== null
        }

        if (!hasAllParams || !allParamsHaveValues) {
          setHasAccess(false)
          setIsLoading(false)
        }

        try {
          const id = searchParams.get('id')
          const qs = searchParams.get('qs')
          const url = COMMUNICATIONSERVICE_BASE_URL ?? undefined

          const response = await axios.get(
            `${url}/api/v1/Unsubscribe/ValidateUnsubscribeEmail?Id=${id}&Qs=${qs}`,
          )

          if (response && response.data) {
            setHasAccess(response.data)
            return
          }

          setHasAccess(false)
        } catch (error) {
          setHasAccess(false)
        } finally {
          setIsLoading(false)
        }
      }
    }

    validateAccess()
  }, [])

  // If protected route checks are still loading then show empty div
  if (isLoading) {
    return <Box />
  }

  // If the user has access to the component, render it.
  return <> {hasAccess ? children : <ProtectedRouteError />}</>
}

export default UnsubscribeProtectedRoute
